// "Diventa cliente": chi entra senza essere in anagrafica (ospite) manda i suoi dati allo studio, che li
// controlla e crea il cliente. Facoltativo (si può rimandare: regole Apple). Solo in italiano.
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, UserPlus, X, CheckCircle2, AlertTriangle } from 'lucide-react';
import { chiama } from '../../pages/areaClientiApi';

const CHIAVE_RIMANDA = 'jv_area_clienti_registrazione_dopo';
const VUOTO = { last_name: '', first_name: '', gender: '', birth_date: '', birth_abroad: false, birth_place_code: '', luogo: '',
  fiscal_code: '', address: '', address_number: '', postal_code: '', city: '', city_provincia: '' };
const stile = 'mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-[16px] bg-white uppercase';

function Campo({ etichetta, children }) {
  return <label className="block"><span className="text-[13px] font-medium text-slate-600">{etichetta}</span>{children}</label>;
}

// un solo campo per comuni italiani e Stati esteri (prima serviva scegliere «All'estero»: chi scriveva «Cina»
// trovava solo CINAGLIO e non poteva scegliere)
function Luogo({ token, valore, onScegli }) {
  const [q, setQ] = useState(valore || '');
  const [elenco, setElenco] = useState([]);
  useEffect(() => { setQ(valore || ''); }, [valore]);
  useEffect(() => {
    if (q.trim().length < 2 || q === valore) { setElenco([]); return undefined; }
    const cerca = (estero) => chiama(`/registrazione/luoghi?q=${encodeURIComponent(q.trim())}&estero=${estero}`, { token })
      .then((r) => r.luoghi).catch(() => []);
    const t = setTimeout(() => Promise.all([cerca(0), cerca(1)]).then(([comuni, stati]) => {
      const testo = q.trim().toUpperCase();
      const tutti = [...stati, ...comuni];
      const esatti = tutti.filter((l) => l.nome === testo);
      setElenco([...esatti, ...tutti.filter((l) => l.nome !== testo)].slice(0, 12));
    }), 250);
    return () => clearTimeout(t);
  }, [q, token, valore]);
  return (
    <div className="relative">
      <input value={q} onChange={(e) => { setQ(e.target.value); onScegli(null); }} className={stile}
        placeholder="Comune o Stato estero (es. Prato, Cina)" />
      {elenco.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-56 overflow-y-auto">
          {elenco.map((l) => (
            <button key={l.codice} type="button" onClick={() => { onScegli(l); setElenco([]); }}
              className="w-full text-left px-3 py-2 text-[15px] hover:bg-green-50 flex justify-between gap-2">
              <span>{l.etichetta}</span>{l.estero && <span className="text-[12px] text-slate-500">Stato estero</span>}
            </button>
          ))}
        </div>
      )}
      {q.trim().length >= 2 && q !== valore && <p className="text-[12px] text-slate-500 mt-1">Scegli dall'elenco il comune o lo Stato in cui sei nato.</p>}
    </div>
  );
}

function Modulo({ token, iniziali, onChiudi, onInviato }) {
  const [d, setD] = useState(() => ({ ...VUOTO, ...(iniziali || {}),
    luogo: iniziali ? (iniziali.birth_abroad ? iniziali.birth_country : `${iniziali.birth_comune} (${iniziali.birth_provincia})`) : '' }));
  const [errore, setErrore] = useState('');
  const [avviso, setAvviso] = useState('');
  const [invio, setInvio] = useState(false);
  const set = (k) => (e) => setD((x) => ({ ...x, [k]: e.target.value }));
  const datiCf = () => ({ last_name: d.last_name, first_name: d.first_name, gender: d.gender, birth_date: d.birth_date, birth_place_code: d.birth_place_code });

  const calcola = async () => {
    setErrore(''); setAvviso('');
    try { const r = await chiama('/registrazione/calcola-cf', { metodo: 'POST', corpo: datiCf(), token }); setD((x) => ({ ...x, fiscal_code: r.fiscal_code })); } catch (e) { setErrore(e.message); }
  };
  const invia = async () => {
    setErrore(''); setAvviso(''); setInvio(true);
    try {
      // codice scritto a mano diverso da quello calcolato: lo segnaliamo, ma si può inviare lo stesso (omocodia)
      if (d.fiscal_code && d.last_name && d.birth_place_code && !avviso) {
        const r = await chiama('/registrazione/calcola-cf', { metodo: 'POST', corpo: datiCf(), token }).catch(() => null);
        if (r && r.fiscal_code !== d.fiscal_code.replace(/\s/g, '').toUpperCase()) {
          setAvviso(`Il codice fiscale calcolato dai tuoi dati è ${r.fiscal_code}. Controlla: se il tuo è giusto premi di nuovo «Invia».`);
          setInvio(false);
          return;
        }
      }
      const { luogo, ...corpo } = d;
      await chiama('/registrazione', { metodo: 'POST', corpo, token });
      onInviato();
    } catch (e) { setErrore(e.message); }
    setInvio(false);
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center" onClick={onChiudi}>
      <div className="bg-white w-full max-w-xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="text-[18px] font-bold">Diventa cliente</div>
          <button onClick={onChiudi} className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-[14px] text-slate-600">Completa i tuoi dati: lo studio li controlla e ti registra come cliente. Così vedrai le tue pratiche, potrai prenotare e pubblicare annunci.</p>
        <div className="grid grid-cols-2 gap-2">
          <Campo etichetta="Cognome"><input value={d.last_name} onChange={set('last_name')} className={stile} /></Campo>
          <Campo etichetta="Nome"><input value={d.first_name} onChange={set('first_name')} className={stile} /></Campo>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Campo etichetta="Sesso">
            <div className="mt-1 flex gap-2">
              {[['M', 'Maschio'], ['F', 'Femmina']].map(([k, t]) => (
                <button key={k} type="button" onClick={() => setD((x) => ({ ...x, gender: k }))}
                  className={`flex-1 rounded-xl border py-2.5 text-[15px] font-medium ${d.gender === k ? 'bg-green-800 text-white border-green-800' : 'bg-white border-slate-200'}`}>{t}</button>
              ))}
            </div>
          </Campo>
          <Campo etichetta="Data di nascita"><input type="date" value={d.birth_date} onChange={set('birth_date')} className={stile} /></Campo>
        </div>
        <Campo etichetta="Luogo di nascita (comune o Stato estero)">
          <Luogo token={token} valore={d.luogo}
            onScegli={(l) => setD((x) => ({ ...x, birth_place_code: l ? l.codice : '', birth_abroad: l ? l.estero : x.birth_abroad, luogo: l ? l.etichetta : x.luogo }))} />
        </Campo>
        <Campo etichetta="Codice fiscale">
          <div className="flex gap-2">
            <input value={d.fiscal_code} onChange={(e) => { setAvviso(''); set('fiscal_code')(e); }} maxLength={16} className={`${stile} font-mono`} placeholder="Scrivilo o premi Calcola" />
            <button type="button" onClick={calcola} className="mt-1 shrink-0 rounded-xl bg-slate-100 px-3 text-[14px] font-semibold">Calcola</button>
          </div>
        </Campo>
        <div className="text-[15px] font-semibold pt-1">Residenza</div>
        <div className="grid grid-cols-[1fr_6rem] gap-2">
          <Campo etichetta="Via / piazza"><input value={d.address} onChange={set('address')} className={stile} /></Campo>
          <Campo etichetta="Civico"><input value={d.address_number} onChange={set('address_number')} className={stile} /></Campo>
        </div>
        <div className="grid grid-cols-[6rem_1fr_5rem] gap-2">
          <Campo etichetta="CAP"><input inputMode="numeric" maxLength={5} value={d.postal_code} onChange={set('postal_code')} className={stile} /></Campo>
          <Campo etichetta="Comune"><input value={d.city} onChange={set('city')} className={stile} /></Campo>
          <Campo etichetta="Prov."><input maxLength={2} value={d.city_provincia} onChange={set('city_provincia')} className={stile} /></Campo>
        </div>
        {avviso && <p className="rounded-xl bg-amber-50 text-amber-900 text-[14px] p-3 flex gap-2"><AlertTriangle className="w-5 h-5 shrink-0" />{avviso}</p>}
        {errore && <p className="text-red-700 text-[14px]">{errore}</p>}
        <div className="flex gap-2">
          <button type="button" onClick={onChiudi} className="flex-1 rounded-xl bg-slate-100 font-semibold py-3">Più tardi</button>
          <button type="button" disabled={invio} onClick={invia} className="flex-[2] rounded-xl bg-green-800 disabled:opacity-60 text-white font-semibold py-3 flex items-center justify-center gap-2">
            {invio && <Loader2 className="w-5 h-5 animate-spin" />}Invia
          </button>
        </div>
        <p className="text-[12px] text-slate-500">I dati servono allo studio per registrarti e seguire le tue pratiche (vedi Informativa privacy in Contatti).</p>
      </div>
    </div>
  );
}

// automatico = si apre da solo una volta dopo l'accesso (finché non invia o non sceglie «Più tardi»)
export default function DiventaCliente({ token, automatico }) {
  const [stato, setStato] = useState(null);
  const [aperto, setAperto] = useState(false);
  const carica = useCallback(() => chiama('/registrazione', { token }).then((r) => {
    setStato(r);
    let rimandato = false;
    try { rimandato = sessionStorage.getItem(CHIAVE_RIMANDA) === '1'; } catch (_) { /* privato */ }
    if (automatico && r.ospite && !r.stato && !rimandato) setAperto(true);
  }).catch(() => {}), [token, automatico]);
  useEffect(() => { carica(); }, [carica]);
  const chiudi = () => { setAperto(false); try { sessionStorage.setItem(CHIAVE_RIMANDA, '1'); } catch (_) { /* privato */ } };

  if (!stato || !stato.ospite) return null;
  const modulo = aperto && <Modulo token={token} iniziali={['scartata', 'in_attesa'].includes(stato.stato) ? stato.dati : null} onChiudi={chiudi}
    onInviato={() => { setAperto(false); carica(); }} />;
  if (automatico) return modulo || null;
  return (
    <div className="bg-white rounded-3xl shadow-sm p-5">
      {modulo}
      {stato.stato === 'in_attesa' ? (
        <div className="flex gap-3 items-start">
          <CheckCircle2 className="w-6 h-6 text-green-700 shrink-0" />
          <div>
            <div className="text-[16px] font-semibold">Dati inviati allo studio</div>
            <p className="text-[14px] text-slate-600 mt-1">Appena lo studio ti registra, entrando di nuovo vedrai qui le tue pratiche.</p>
            <button onClick={() => setAperto(true)} className="mt-2 text-[14px] text-green-800 font-semibold">Correggi i dati</button>
          </div>
        </div>
      ) : (
        <div className="flex gap-3 items-start">
          <UserPlus className="w-6 h-6 text-green-700 shrink-0" />
          <div className="flex-1">
            <div className="text-[16px] font-semibold">Diventa cliente</div>
            {stato.stato === 'scartata' && <p className="text-[14px] text-red-700 mt-1">Richiesta non accettata: {stato.motivo}</p>}
            <p className="text-[14px] text-slate-600 mt-1">Completa i tuoi dati per avere tutti i servizi: pratiche, prenotazioni, annunci.</p>
            <button onClick={() => setAperto(true)} className="mt-3 rounded-xl bg-green-800 text-white font-semibold px-4 py-2.5">
              {stato.stato === 'scartata' ? 'Correggi e reinvia' : 'Completa i dati'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
